import React from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { conflictingSources, needsList, painPoints, searchQuestions, symptoms, systemShows } from '../data/painPoints';
const reveal = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }
} as const;
export function PainPoints() {
  return <section className="py-24 px-4 bg-neutral-100" data-id="element-156" data-mp="1xw6j2xx07sbw">
      <div className="max-w-4xl mx-auto" data-id="element-157" data-mp="21z32gqudbsgw">
        <motion.div {...reveal} className="text-center mb-12" data-id="element-158" data-mp="220weymrnx6wt">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 text-neutral-900 uppercase" data-id="element-159" data-mp="1o03ezmildjmb">
            Still Spending Hours Preparing Firefighter Training Sessions?
          </h2>
          <div className="text-lg text-neutral-800 leading-relaxed bg-white p-6 md:p-8 rounded-2xl border border-neutral-200 text-left space-y-4" data-id="element-160" data-mp="3id33k54y9o5q">
            <p data-id="element-161" data-mp="3nbwi80vz3l7h">
              {conflictingSources.map(line => <React.Fragment key={line} data-id="element-162" data-mp="1yzn3jiw8v6fk">
                  {line}
                  <br data-id="element-163" data-mp="1u1tyit6ogigv" />
                </React.Fragment>)}
            </p>
            <p className="text-neutral-900 font-semibold" data-id="element-164" data-mp="27sz3maste2yl">
              {symptoms.map((line, index) => <React.Fragment key={line} data-id="element-165" data-mp="23rnahvaqezln" data-mpi={index}>
                  {line}
                  {index < symptoms.length - 1 && <br data-id="element-166" data-mp="1g1j6vnxjmzun" />}
                </React.Fragment>)}
            </p>
            <p data-id="element-167" data-mp="1b2pszuech6ty">
              {needsList.map((line, index) => <React.Fragment key={line} data-id="element-168" data-mp="1b4kepaxfx2z3" data-mpi={index}>
                  {line}
                  {index < needsList.length - 1 && <br data-id="element-169" data-mp="1p3sikh4jsz9m" />}
                </React.Fragment>)}
            </p>
            <p data-id="element-170" data-mp="1k1ur4zlzuy5l">Suddenly, the same questions appear again:</p>
            <ul className="space-y-2 pl-1" data-id="element-171" data-mp="30h4n6u84tlwu">
              {searchQuestions.map(question => <li key={question} className="flex items-start gap-3 text-neutral-900 font-semibold" data-id="element-172" data-mp="2vj2vxm0jygaa">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-700 shrink-0 mt-3" aria-hidden="true" data-id="element-173" data-mp="1t5okv3892hzy" />
                  <span data-id="element-174" data-mp="1y3gk6ib856e6">{question}</span>
                </li>)}
            </ul>
            <p className="text-neutral-900 font-semibold" data-id="element-175" data-mp="225lc9cm0xuyp">
              You already understand the importance of firefighter education.
            </p>
            <p data-id="element-176" data-mp="272tuteb3410h">
              But finding, organizing and preparing quality teaching material can become a second
              job.
            </p>
            <p data-id="element-177" data-mp="1efhalasec5ac">
              And when every training topic comes from a different source... preparing the next
              lesson takes far more time than it should.
            </p>
          </div>
        </motion.div>

        <motion.div {...reveal} data-id="element-178" data-mp="1o6u0hwumwldq">
          <h3 className="text-xl font-bold text-neutral-900 mb-8 text-center uppercase tracking-wide" data-id="element-179" data-mp="1o7wre4ryintn">
            The Real Cost of Training Without a Complete System
          </h3>
          <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm shadow-neutral-900/5" data-id="element-180" data-mp="vny8r7a3yx9w">
            {painPoints.map((point, index) => <motion.p key={point} initial={{
            opacity: 0,
            x: -12
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.25,
            delay: Math.min(index * 0.04, 0.3),
            ease: [0.23, 1, 0.32, 1]
          }} className="flex items-start gap-3 px-5 py-4 text-neutral-800 text-sm leading-relaxed border-t first:border-t-0 border-neutral-200" data-id="element-181" data-mp="j86xclxnpec7" data-mpi={index}>
                <X size={16} className="text-red-700/70 shrink-0 mt-0.5" aria-hidden="true" data-id="element-182" data-mp="3rmp7ct75r3yg" />
                {point}
              </motion.p>)}
          </div>

          <div className="text-center mt-12 pt-8 border-t border-neutral-200" data-id="element-183" data-mp="3mlsy0041epln">
            <p className="text-2xl font-bold tracking-tight text-neutral-900" data-id="element-184" data-mp="3ilhbsiw10h82">
              You don’t need more disconnected firefighter training information.
            </p>
            <p className="text-xl mt-4 text-neutral-800" data-id="element-185" data-mp="1hl42l3rojyxb">
              You need a <span className="text-red-700 font-semibold" data-id="element-186" data-mp="1llefl0dw3plo">system</span> that shows you:
            </p>
            <ul className="mt-5 space-y-1.5" data-id="element-187" data-mp="17uritdrow9s8">
              {systemShows.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-red-700" data-id="element-188" data-mp="1ct2vdw9dcye5">
                  {line}
                </li>)}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>;
}